// The recurring "Stay Updated" band. Rendered by SiteFooter on every public
// page, and again as a full-bleed photo band on About.
import { useState } from 'react';
import { COLORS, FONTS, HEADING, TYPE } from 'styles/theme';
import { BUTTON, PAD, SPACE, SURFACE } from 'styles/blocks';
import { subscribeNewsletter } from 'services/publicFlowService';

/**
 * @param source   analytics tag passed through to /newsletter/subscribe
 * @param onDark   invert the copy for a dark or photo background
 * @param image    optional photo behind the band; the sand ground shows through
 *                 if the file is missing, so a bad path degrades to a plain band
 * @param style    background/padding for the surrounding band
 */
export default function NewsletterSignup({ source = 'footer', onDark = false, image, style }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || state === 'sending') return;
    setState('sending');
    const res = await subscribeNewsletter(email.trim(), source);
    if (res.success) setEmail('');
    setState(res.success ? 'done' : 'error');
  };

  const ink = onDark ? '#fff' : COLORS.dark;

  // A flat-lay needs vertical room or `cover` crops it to a sliver, so the photo
  // variant is a tall band with the copy centred in it rather than a padded strip.
  const photo = image && {
    backgroundImage: `url(${image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '620px',
    padding: `${SPACE.bandY * 2}px ${PAD}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // `photo` spreads after `style` on purpose: a `background` shorthand in
  // `style` resets background-image, so the layers must be declared after it.
  return (
    <section style={{
      padding: `${SPACE.bandY}px ${PAD}px`, textAlign: 'center', color: ink,
      ...style, ...photo,
    }}>
      <div style={{ width: '100%', maxWidth: '620px', margin: '0 auto' }}>
        <h2 style={{ ...HEADING, fontSize: TYPE.sectionHeading, margin: '0 0 12px', color: ink }}>
          Stay Updated
        </h2>
        <p style={{ fontSize: TYPE.body, lineHeight: 1.7, margin: '0 0 28px', color: onDark ? '#fff' : COLORS.text }}>
          Subscribe with your email address for news and updates.
        </p>
        {state === 'done' ? (
          <p style={{ fontSize: '17px', fontWeight: 700, color: onDark ? '#fff' : '#2c5f2c' }} role="status">
            Thank you!
          </p>
        ) : (
          <form onSubmit={onSubmit} style={{ display: 'inline-flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <input
              type="email" required placeholder="Email Address" aria-label="Email Address"
              value={email} onChange={(e) => setEmail(e.target.value)}
              style={{
                padding: '15px 18px', fontSize: '16px', fontFamily: FONTS.sans, minWidth: '280px',
                border: `1.5px solid ${onDark ? 'transparent' : SURFACE.border}`, borderRadius: '6px',
                background: '#fff', color: COLORS.dark,
              }}
            />
            <button type="submit" disabled={state === 'sending'} style={{
              ...BUTTON, border: 'none', cursor: 'pointer', opacity: state === 'sending' ? 0.6 : 1,
            }}>
              {state === 'sending' ? 'Signing Up…' : 'Sign Up'}
            </button>
          </form>
        )}
        {state === 'error' && (
          <p style={{ color: onDark ? '#ffd7d7' : '#cc3333', fontSize: '14px', margin: '12px 0 0' }} role="alert">
            Could not subscribe — please try again.
          </p>
        )}
      </div>
    </section>
  );
}
